<template>
  <div class="flow-tabs-wrapper">
    <div v-if="isLoading" class="flow-tabs-loading">
      <BaseLoadingSpinner :with-overlay="false" :size="60" />
    </div>
    <div v-else-if="hasError" class="flow-tabs-error">
      <p>Kunde inte ladda rörelsedata</p>
    </div>
    <template v-else-if="dataReady">
      <div class="flow-tabs-bar">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="flow-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
        <div class="flow-tabs-count">
          {{ totalSwaps }} ärenden visualiserade
        </div>
      </div>

      <FlowMap
        v-show="activeTab === 'map'"
        :nodes="nodes"
        :raw-flows="rawFlows"
        :links="links"
        :max-flow-count="maxFlowCount"
        :color-map="colorMap"
      />
      <FlowMatrix
        v-if="activeTab === 'matrix'"
        :nodes="nodes"
        :raw-flows="rawFlows"
        :color-map="colorMap"
      />
      <FlowInsights
        v-if="activeTab === 'insights'"
        :nodes="nodes"
        :raw-flows="rawFlows"
      />
      <FlowProfiles
        v-if="activeTab === 'profiles'"
        :nodes="nodes"
        :raw-flows="rawFlows"
        :color-map="colorMap"
        @select-city="selectCityOnMap"
      />
    </template>
  </div>
</template>

<script>
import { getSwapGeography } from '@/services/baService';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import FlowMap from '@/components/FlowMap.vue';
import FlowMatrix from '@/components/FlowMatrix.vue';
import FlowInsights from '@/components/FlowInsights.vue';
import FlowProfiles from '@/components/FlowProfiles.vue';

const PALETTE = {
  green: '#3A6B44',
  teal: '#2A7B6F',
  purple: '#6B4A8A',
  rose: '#B84040',
  gold: '#9A7B2A',
  slate: '#3A5A72',
  amber: '#C8822A'
};

const FLOW_COLORS = [
  PALETTE.green,
  PALETTE.teal,
  PALETTE.purple,
  PALETTE.rose,
  PALETTE.gold,
  PALETTE.slate,
  PALETTE.amber,
  '#507090',
  '#90605a',
  '#609060'
];

export default {
  components: {
    BaseLoadingSpinner,
    FlowMap,
    FlowMatrix,
    FlowInsights,
    FlowProfiles
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      dataReady: false,
      activeTab: 'map',
      nodes: [],
      links: [],
      rawFlows: [],
      maxFlowCount: 1,
      colorMap: {},
      totalSwaps: 0,
      tabs: [
        { id: 'map', label: 'Karta' },
        { id: 'matrix', label: 'Flödesmatris' },
        { id: 'insights', label: 'Insikter' },
        { id: 'profiles', label: 'Ortsprofiler' }
      ]
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const data = await getSwapGeography();
        this.processData(data);
        this.dataReady = true;
      } catch (error) {
        console.error('FlowTabs error:', error);
        this.hasError = true;
      }
      this.isLoading = false;
    },

    processData(rows) {
      const nodeMap = new Map();
      const flowMap = new Map();
      let totalSwaps = 0;

      for (const row of rows) {
        if (!row.fromLat || !row.toLat) continue;

        // Prefer area (stadsdel) over city for granularity
        const fromKey = (row.fromArea || row.fromCity || '').trim();
        const toKey = (row.toArea || row.toCity || '').trim();
        if (!fromKey || !toKey) continue;
        totalSwaps++;

        if (!nodeMap.has(fromKey)) {
          nodeMap.set(fromKey, {
            id: fromKey,
            label: fromKey,
            lat: Number(row.fromLat),
            lng: Number(row.fromLng),
            outgoing: 0,
            incoming: 0,
            incomes: []
          });
        }
        if (!nodeMap.has(toKey)) {
          nodeMap.set(toKey, {
            id: toKey,
            label: toKey,
            lat: Number(row.toLat),
            lng: Number(row.toLng),
            outgoing: 0,
            incoming: 0,
            incomes: []
          });
        }

        const income = Number(row.income);
        if (income > 0) {
          nodeMap.get(fromKey).incomes.push(income);
        }

        if (fromKey === toKey) {
          const node = nodeMap.get(fromKey);
          node.internal = (node.internal || 0) + 1;
          continue;
        }

        nodeMap.get(fromKey).outgoing++;
        nodeMap.get(toKey).incoming++;

        const flowKey = `${fromKey}->${toKey}`;
        if (flowMap.has(flowKey)) {
          flowMap.get(flowKey).count++;
        } else {
          flowMap.set(flowKey, { from: fromKey, to: toKey, count: 1 });
        }
      }

      if (!nodeMap.size) {
        this.hasError = true;
        return;
      }

      this.totalSwaps = totalSwaps;
      this.rawFlows = Array.from(flowMap.values());
      this.maxFlowCount = Math.max(...this.rawFlows.map(f => f.count), 1);

      // Compute median income per node
      for (const node of nodeMap.values()) {
        if (node.incomes.length) {
          const sorted = [...node.incomes].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          node.medianIncome =
            sorted.length % 2 === 0
              ? (sorted[mid - 1] + sorted[mid]) / 2
              : sorted[mid];
        } else {
          node.medianIncome = null;
        }
        delete node.incomes;
      }

      this.nodes = Array.from(nodeMap.values());

      this.links = this.rawFlows.map(f => ({
        source: f.from,
        target: f.to,
        count: f.count
      }));

      const destinations = [...new Set(this.rawFlows.map(f => f.to))].sort(
        (a, b) => {
          const ca = this.rawFlows
            .filter(f => f.to === a)
            .reduce((s, f) => s + f.count, 0);
          const cb = this.rawFlows
            .filter(f => f.to === b)
            .reduce((s, f) => s + f.count, 0);
          return cb - ca;
        }
      );
      const colorMap = {};
      destinations.forEach((dest, i) => {
        colorMap[dest] = FLOW_COLORS[i % FLOW_COLORS.length];
      });
      this.colorMap = colorMap;
    },

    selectCityOnMap() {
      this.activeTab = 'map';
    }
  }
};
</script>

<style scoped>
.flow-tabs-wrapper {
  margin: -24px;
  overflow: hidden;
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  border-top: none;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
}

.flow-tabs-bar {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e6e6e6;
  background: #f8f8f8;
  align-items: center;
}

.flow-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #7a6e5f;
  padding: 10px 20px;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  font-size: 1.1rem;

  color: #878b9d;
}

.flow-tab.active {
  font-weight: 700;
  color: #000;
  border-bottom-color: #000;
}

.flow-tab:hover:not(.active) {
  color: #1a1209;
}

.flow-tabs-count {
  margin-left: auto;
  padding-right: 16px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #000;
}

.flow-tabs-loading,
.flow-tabs-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.flow-tabs-error p {
  font-size: 1.6rem;
  color: #2c3e50;
}
</style>
